'use client';

import { useCallback, useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  IconButton,
  Badge,
  Popover,
  Text,
  Tooltip,
  toasterApi,
} from '@terra/ui-design-system';
import { Divider, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  PlayIcon,
  Radar01Icon,
  SquareIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { TriggerScanType } from '@terra/shared-contracts-external-network';
import {
  useStopExternalNetworkScan,
  useTriggerExternalNetworkScan,
} from '../../hooks/api/useExternalNetworkScanActions';
import { useActiveExternalNetworkScan } from '../../hooks/api/useExternalNetworkScans';

const SCAN_TYPE_STORAGE_KEY = 'terra.external-network.preferred-scan-type';
const SCAN_TYPES = [
  'RECON',
  'DISCOVERY',
  'FULL',
] as const satisfies readonly TriggerScanType[];

type TopBarExternalNetworkScanButtonProps = {
  surfaceId: string;
};

export function TopBarExternalNetworkScanButton({
  surfaceId,
}: TopBarExternalNetworkScanButtonProps) {
  const t = useTranslations('externalNetwork');
  const tCommon = useTranslations('common');
  const triggerScan = useTriggerExternalNetworkScan();
  const stopScan = useStopExternalNetworkScan();
  const { activeScan } = useActiveExternalNetworkScan({ surfaceId });
  const [isOpen, setIsOpen] = useState(false);
  const [stopTarget, setStopTarget] = useState<string | null>(null);
  const triggerExternalNetworkScan = useCallback(
    (scanType: TriggerScanType) => {
      triggerScan.mutate(
        { surfaceId, scanType },
        {
          onSuccess: () => {
            setIsOpen(false);
            toasterApi.success({
              title: t('scanStartedTitle'),
              description: t('scanStartedDescription'),
            });
          },
          onError: () => {
            toasterApi.error({
              title: tCommon('error'),
              description: t('scanTriggerFailed'),
            });
          },
        },
      );
    },
    [surfaceId, t, tCommon, triggerScan],
  );
  const handleSelectScanType = useCallback(
    (scanType: TriggerScanType) => {
      try {
        localStorage.setItem(SCAN_TYPE_STORAGE_KEY, scanType);
      } catch (error) {
        void error;
      }

      triggerExternalNetworkScan(scanType);
    },
    [triggerExternalNetworkScan],
  );
  const isScanning = Boolean(activeScan) || triggerScan.isPending;

  return (
    <>
      <Popover.Root
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        positioning={{ placement: 'bottom-end', strategy: 'fixed' }}
        closeOnInteractOutside
      >
        <Popover.Trigger asChild>
          <Button
            variant="ghost"
            size="xs"
            aria-label={t('scan')}
            disabled={triggerScan.isPending}
          >
            <Radar01Icon size={14} />
            {isScanning && (
              <Badge variant="fill" size="sm">
                1
              </Badge>
            )}
            {isOpen ? (
              <ArrowUp01Icon size={12} />
            ) : (
              <ArrowDown01Icon size={12} />
            )}
          </Button>
        </Popover.Trigger>

        <Popover.Positioner>
          <Popover.Content
            minW="280px"
            maxW="320px"
            maxH="80vh"
            overflowY="auto"
            bg="bg.default"
            borderRadius="lg"
            boxShadow="xl"
            p="3"
          >
            <VStack gap="3" w="full">
              <HStack justifyContent="space-between" w="full" px="1">
                <HStack gap="2" alignItems="center">
                  <Radar01Icon size={16} />
                  <Text fontSize="sm" fontWeight="semibold">
                    {t('scan')}
                  </Text>
                </HStack>
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={() => setIsOpen(false)}
                  aria-label={tCommon('close')}
                >
                  <Cancel01Icon size={14} />
                </IconButton>
              </HStack>

              <Divider />

              {activeScan ? (
                <HStack
                  w="full"
                  justifyContent="space-between"
                  alignItems="center"
                  px="2"
                  py="2"
                  borderRadius="md"
                  bg="bg.subtle"
                >
                  <Text fontSize="sm">
                    {t(`scanType.${activeScan.scanType}`)}
                  </Text>
                  <Tooltip title={t('stopScan')}>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      disabled={stopScan.isPending}
                      loading={stopScan.isPending}
                      onClick={() => setStopTarget(activeScan.id)}
                      aria-label={t('stopScan')}
                    >
                      <SquareIcon size={14} />
                    </IconButton>
                  </Tooltip>
                </HStack>
              ) : (
                <VStack gap="1" w="full">
                  {SCAN_TYPES.map((scanType) => (
                    <HStack
                      key={scanType}
                      w="full"
                      justifyContent="space-between"
                      alignItems="center"
                      px="2"
                      py="2"
                      borderRadius="md"
                      _hover={{ bg: 'bg.subtle' }}
                    >
                      <Text fontSize="sm">{t(`scanType.${scanType}`)}</Text>
                      <Tooltip title={t(`scanType.${scanType}`)}>
                        <IconButton
                          variant="ghost"
                          size="xs"
                          disabled={triggerScan.isPending}
                          loading={triggerScan.isPending}
                          onClick={() => handleSelectScanType(scanType)}
                          aria-label={t(`scanType.${scanType}`)}
                        >
                          <PlayIcon size={14} />
                        </IconButton>
                      </Tooltip>
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>

      <ConfirmationDialog
        isOpen={Boolean(stopTarget)}
        onOpenChange={({ open }) => {
          if (!open) {
            setStopTarget(null);
          }
        }}
        onConfirm={async () => {
          if (!stopTarget) {
            return;
          }

          try {
            await stopScan.mutateAsync({ surfaceId, scanId: stopTarget });
            setIsOpen(false);
          } catch (error) {
            toasterApi.error({
              title: tCommon('error'),
              description: t('scanStopFailed'),
            });

            throw error;
          }
        }}
        loading={stopScan.isPending}
        variant="destructive"
        title={t('stopConfirmTitle')}
        description={t('stopConfirmDescription')}
        confirmText={t('stopConfirm')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
